<template>
  <div>
    <Panel :icon="['fas', 'money-check-alt']">
      <template #header>
        <span data-cy="heading">Account Summary</span>
      </template>

      <template #content>
        <div class="w-100">
          <div class="billing-widgets-grid">
            <!-- Available Balance Widget -->
            <div class="billing-widget widget-balance clickable-widget" @click="$bvModal.show('modal-balance-detail')">
              <div class="widget-icon">
                <font-awesome-icon :icon="['fas', 'wallet']" />
              </div>
              <div class="widget-content">
                <span class="widget-label">Available Balance</span>
                <span
                  class="widget-value"
                  :class="{'text-danger': isLowBalance}"
                >
                  {{ formatCurrency(availableBalance) }}
                </span>
                <small v-if="availableBalance <= 0 && isPostPay" class="widget-subtext text-danger mt-1" style="font-weight: 600;">
                  Top-up required to buy calls
                </small>
                <small v-if="isBalanceLow && availableBalance > 0" class="widget-subtext text-danger mt-1" style="font-weight: 600;">
                  Balance is low
                </small>
                <small v-if="isDailyBudgetReached" class="widget-subtext text-warning mt-1" style="font-weight: 600;">
                  Daily budget reached
                </small>
              </div>
              <div class="widget-decoration">
                <font-awesome-icon :icon="['fas', 'wallet']" />
              </div>
            </div>

            <!-- Calls Purchased Widget -->
            <div
              v-if="isPostPay && canViewCallLogs"
              class="billing-widget widget-calls clickable-widget"
              v-b-tooltip.hover
              title="Estimated usage charges for the current billing cycle. These have not yet been invoiced."
              @click="openUnbilledUsageModal"
            >
              <div class="widget-icon">
                <font-awesome-icon :icon="['fas', 'phone-volume']" />
              </div>
              <div class="widget-content">
                <span class="widget-label">Unbilled Usage</span>
                <div class="d-flex flex-column">
                  <span class="widget-value">{{ formatCurrency(purchasedCallsAmount) }}</span>
                  <small class="widget-subtext">{{ purchasedCallsCount }} calls since last cycle</small>
                </div>
              </div>
              <div class="widget-decoration">
                <font-awesome-icon :icon="['fas', 'chart-line']" />
              </div>
            </div>

            <!-- Unpaid Invoices Widget -->
            <div v-if="isPostPay" class="billing-widget widget-unpaid clickable-widget" @click="$bvModal.show('modal-unpaid-invoices')">
              <div class="widget-icon">
                <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" />
              </div>
              <div class="widget-content">
                <span class="widget-label">Payment Required</span>
                <div class="d-flex flex-column">
                  <span class="widget-value">{{ formatCurrency(unpaidInvoices.total) }}</span>
                  <small class="widget-subtext">{{ unpaidInvoices.count }} invoices pending</small>
                </div>
              </div>
              <div class="widget-decoration">
                <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" />
              </div>
            </div>

            <!-- Pending Payments (Seller only) -->
            <div
              v-if="!isBuyerAccount"
              class="billing-widget widget-pending clickable-widget"
              @click="$bvModal.show('modal-unpaid-invoices')"
            >
              <div class="widget-icon">
                <font-awesome-icon :icon="['fas', 'hand-holding-usd']" />
              </div>
              <div class="widget-content">
                <span class="widget-label">Pending Payments</span>
                <span class="widget-value">{{ formatCurrency(sellerPendingBalance) }}</span>
              </div>
              <div class="widget-decoration">
                <font-awesome-icon :icon="['fas', 'hand-holding-usd']" />
              </div>
            </div>
          </div>
        </div>
      </template>
    </Panel>

    <div 
      v-if="showTabs"
      class="mt-4" 
    >
      <b-tabs 
        v-model="tabIndex"
        content-class="mt-3"
      >
        <b-tab
          v-if="showCreditCardCharges"
          title="Payment Receipts"
        >
          <CreditCardCharges />
        </b-tab>
        <b-tab
          v-if="showInvoices"
          title="Payment History"
        >
          <Invoices />
        </b-tab>
        <b-tab
          v-if="canViewBilling"
          title="Balance History"
        >
          <BalanceHistory />
        </b-tab>
      </b-tabs>
    </div>

    <!-- Payment Required Modal -->
    <b-modal
      id="modal-unpaid-invoices"
      title="Unpaid Invoices"
      size="lg"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      ok-only
      ok-title="Close"
    >
      <b-table
        :fields="unpaidInvoiceFields"
        :items="unpaidInvoicesList"
        thead-class="text-nowrap"
        tbody-tr-class="text-nowrap"
        show-empty
        empty-text="No unpaid invoices"
      >
        <template #cell(invoiceId)="row">
          {{ row.item.invoiceId || '-' }}
        </template>
        <template #cell(dateDue)="row">
          {{ dateFormat(row.item.dateDue, 'MMM DD, YYYY') }}
        </template>
        <template #cell(amountDue)="row">
          {{ formatCurrency(row.item.amountDue) }}
        </template>
        <template #cell(status)="row">
          <b-badge
            tag="div"
            class="w-100 py-1"
            :variant="getUnpaidBadgeVariant(row.item.paymentStatusId)"
          >
            {{ row.item.paymentStatusId }}
          </b-badge>
        </template>
      </b-table>
    </b-modal>

    <!-- Unbilled Usage Modal -->
    <b-modal
      id="modal-unbilled-usage"
      title="Unbilled Calls"
      size="lg"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      ok-only
      ok-title="Close"
    >
      <div v-if="unbilledCallsLoading" class="text-center py-4">
        <b-spinner variant="info" />
      </div>
      <template v-else>
        <b-table
          :fields="unbilledCallFields"
          :items="unbilledCallsLimited"
          thead-class="text-nowrap"
          tbody-tr-class="text-nowrap"
          show-empty
          empty-text="No unbilled calls found"
        >
          <template #cell(timestamp)="row">
            {{ dateFormat(row.item.timestamp, 'MMM DD, YYYY hh:mm A') }}
          </template>
          <template #cell(buyerCampaignName)="row">
            {{ row.item.buyerCampaignName || '-' }}
          </template>
          <template #cell(durationInSeconds)="row">
            {{ secondsToDuration(row.item.durationInSeconds) }}
          </template>
          <template #cell(cost)="row">
            {{ formatCurrency(row.item.cost) }}
          </template>
        </b-table>
        <div v-if="unbilledCalls.length" class="d-flex justify-content-between align-items-center mt-2">
          <div v-if="unbilledCalls.length > 10" class="text-muted small">
            Showing 10 most recent calls.
            <router-link :to="{ name: 'BuyerCallLogs' }">View all call logs</router-link>
          </div>
          <div class="ml-auto">
            <strong>Total: {{ formatCurrency(purchasedCallsAmount) }}</strong>
          </div>
        </div>
      </template>
    </b-modal>

    <!-- Available Balance Modal -->
    <b-modal
      id="modal-balance-detail"
      title="Balance Breakdown"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      ok-only
      ok-title="Close"
    >
      <div class="balance-breakdown">
        <div class="breakdown-row">
          <span class="breakdown-label">Currency</span>
          <span class="breakdown-value">{{ accountCurrency }}</span>
        </div>
        <div class="breakdown-row">
          <span class="breakdown-label">Cash Balance</span>
          <span class="breakdown-value">{{ formatCurrency(cashBalance) }}</span>
        </div>
        <template v-if="creditLimit > 0">
          <div class="breakdown-row">
            <span class="breakdown-label">Credit Limit</span>
            <span class="breakdown-value">{{ formatCurrency(creditLimit) }}</span>
          </div>
          <div class="breakdown-row">
            <span class="breakdown-label">Credit Used</span>
            <span class="breakdown-value text-warning">{{ formatCurrency(Math.max(0, creditUsed)) }}</span>
          </div>
        </template>
        <hr class="breakdown-divider">
        <div class="breakdown-row">
          <span class="breakdown-label">Available Balance</span>
          <span class="breakdown-value text-success">{{ formatCurrency(availableBalance) }}</span>
        </div>
        <hr class="breakdown-divider">
        <div class="breakdown-row">
          <span class="breakdown-label">Low Balance</span>
          <span class="breakdown-value" :class="isBalanceLow ? 'text-danger' : 'text-success'">
            {{ isBalanceLow ? 'Yes' : 'No' }}
          </span>
        </div>
        <div class="breakdown-row">
          <span class="breakdown-label">Daily Budget Reached</span>
          <span class="breakdown-value" :class="isDailyBudgetReached ? 'text-warning' : 'text-success'">
            {{ isDailyBudgetReached ? 'Yes' : 'No' }}
          </span>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import formatCurrency from '@/utils/formatCurrency'
import dateFormatMixin from '@/mixins/dateFormatMixin'
import BuyerCallLogsAPI from '@/api/buyer-call-logs.api'
import { showErrorMessage } from '@/notification-utils'
import CreditCardCharges from '../buyer/CreditCardCharges.vue'
import Invoices from '../billing/Invoices.vue'
import BalanceHistory from './BalanceHistory.vue'

export default {
  name: 'MyAccount',

  title: 'My Account',

  components: {
    CreditCardCharges,
    Invoices,
    BalanceHistory
  },

  mixins: [dateFormatMixin],

  data() {
    return {
      loading: false,
      details: {},
      tabIndex: 0,
      unbilledCalls: [],
      unbilledCallsLoading: false,
      unpaidInvoiceFields: [
        { key: 'invoiceId', label: 'Invoice #' },
        { key: 'dateDue', label: 'Due Date', sortable: true },
        { key: 'amountDue', label: 'Amount' },
        { key: 'status', label: 'Status' }
      ],
      unbilledCallFields: [
        { key: 'timestamp', label: 'Date' },
        { key: 'buyerCampaignName', label: 'Campaign' },
        { key: 'durationInSeconds', label: 'Duration' },
        { key: 'cost', label: 'Cost' }
      ]
    }
  },
  computed: {
    ...mapState('billing', ['accountSummary', 'accountPayments', 'purchasedCalls', 'accountBalance']),
    ...mapState('dashboard', ['chargeReceipts', 'creditCardChargesFetched']),
    ...mapGetters('auth', ['isBuyerAccount', 'accountDetails', 'hasPermission']),
    ...mapGetters('billing', ['sellerPendingBalance', 'accountBillingConfiguration']),
    canViewCallLogs() {
      return this.hasPermission('call_logs')
    },
    unbilledCallsLimited() {
      return this.unbilledCalls.slice(0, 10)
    },
    availableBalance() {
      if (!this.accountSummary) return 0

      let balance = this.accountSummary.cashBalance ?? 0
      // If post-pay, calculating remaining credit: Current Balance - Minimum Balance (which is negative)
      if (this.isPostPay) {
        const minBalance = this.accountBillingConfiguration?.minimumBalance || 0
        balance = balance - minBalance
      }
      
      // Ensure we never show negative available balance
      return Math.max(0, balance)
    },
    isPostPay() {
      return this.isBuyerAccount && this.accountDetails?.billingConfiguration?.prePay === false
    },
    isLowBalance() {
      if (!this.isPostPay) return false
      
      const limit = Math.abs(this.accountBillingConfiguration?.minimumBalance || 0)
      if (limit === 0) return false

      // Highlight if available balance is less than 5% of the total credit limit
      return this.availableBalance < (limit * 0.05)
    },
    purchasedCallsAmount() {
      return this.purchasedCalls?.purchasedCallsAmount || 0
    },
    purchasedCallsCount() {
      return this.purchasedCalls?.purchasedCallsCount || 0
    },
    unpaidInvoices() {
      if (!this.accountPayments || !this.accountPayments.length) {
        return { count: 0, total: 0 }
      }
      // Filter for statuses considered 'Unpaid'
      const unpaidStatuses = ['Outstanding', 'Overdue', 'Failed']
      
      const unpaid = this.accountPayments.filter(inv => unpaidStatuses.includes(inv.paymentStatusId))
      
      const total = unpaid.reduce((sum, inv) => sum + (inv.amountDue || 0), 0)
      
      return {
        count: unpaid.length,
        total: total
      }
    },
    canViewBilling() {
      return this.hasPermission('billing')
    },
    showCreditCardCharges() {
      if (!this.isBuyerAccount || !this.canViewBilling) {
        return false
      }
      const isPrePay = this.accountDetails?.billingConfiguration?.prePay
      // Pre-pay buyers always see credit card charges
      if (isPrePay) {
        return true
      }
      // Post-paid buyers only see credit card charges if they have any
      // Wait for the fetch to complete before deciding to avoid race condition
      return this.creditCardChargesFetched && this.chargeReceipts && this.chargeReceipts.length > 0
    },
    showInvoices() {
      return !this.isBuyerAccount || !this.accountDetails?.billingConfiguration?.prePay
    },
    showTabs() {
      return this.canViewBilling || this.showInvoices
    },
    unpaidInvoicesList() {
      if (!this.accountPayments || !this.accountPayments.length) return []
      const unpaidStatuses = ['Outstanding', 'Overdue']
      return this.accountPayments
        .filter(inv => unpaidStatuses.includes(inv.paymentStatusId))
        .sort((a, b) => new Date(b.dateDue) - new Date(a.dateDue))
    },
    creditLimit() {
      return Math.abs(this.accountBillingConfiguration?.minimumBalance || 0)
    },
    creditUsed() {
      return this.creditLimit - this.availableBalance
    },
    cashBalance() {
      return this.accountSummary?.cashBalance || 0
    },
    isBalanceLow() {
      return this.accountBalance?.isBalanceLow || false
    },
    isDailyBudgetReached() {
      return this.accountBalance?.isDailyBudgetReached || false
    },
    accountCurrency() {
      return this.accountBalance?.accountCurrency || 'USD'
    },
  },
  watch: {
    showCreditCardCharges(val) {
      if (val) {
        this.$nextTick(() => {
          this.tabIndex = 0
        })
      }
    }
  },
  async created() {
    await this.getAccountBillingSummary()
    // Load payments for both buyer and seller (needed for unpaid invoices modal)
    this.getAccountPayments()
    if (this.isBuyerAccount) {
      if (this.canViewCallLogs) this.fetchPurchasedCalls()
      this.getBuyerAccountBalance()
      if (this.canViewBilling) await this.getCreditCardCharges()
    }
  },
  methods: {
    ...mapActions('billing', ['getAccountBillingSummary', 'getAccountPayments', 'fetchPurchasedCalls', 'getBuyerAccountBalance']),
    ...mapActions('dashboard', ['getCreditCardCharges']),
    formatCurrency,

    getUnpaidBadgeVariant(status) {
      const map = { Failed: 'danger', Outstanding: 'secondary', Overdue: 'warning' }
      return map[status] || 'secondary'
    },

    async openUnbilledUsageModal() {
      if (!this.canViewCallLogs) return
      this.$bvModal.show('modal-unbilled-usage')
      this.unbilledCallsLoading = true
      try {
        const accountId = this.$store.getters['auth/accountId']
        const timeZone = this.$store.getters['auth/accountDetails']?.timeZoneId || 'UTC'

        const now = new Date()
        let fromDate

        if (this.accountPayments && this.accountPayments.length > 0) {
          const latestInvoice = this.accountPayments.reduce((latest, current) => {
            if (!current.billingCycleEndDate) return latest
            const currentDate = new Date(current.billingCycleEndDate)
            const latestDate = new Date(latest.billingCycleEndDate)
            return currentDate > latestDate ? current : latest
          }, this.accountPayments[0])

          if (latestInvoice && latestInvoice.billingCycleEndDate) {
            fromDate = new Date(latestInvoice.billingCycleEndDate)
          }
        }

        if (!fromDate) {
          fromDate = new Date(now.getFullYear(), now.getMonth(), 1)
          fromDate.setHours(0, 0, 0, 0)
        }

        const response = await BuyerCallLogsAPI.getBuyerCallLogs(accountId, {
          from: fromDate,
          to: now,
          timeZone,
          pageSize: 100,
          pageNumber: 1
        })

        const calls = response.data?.items || response.data || []
        this.unbilledCalls = calls.filter(call => call.cost > 0)
      } catch (error) {
        this.unbilledCalls = []
        showErrorMessage(error?.response?.data?.title ?? 'Could not load unbilled calls')
      } finally {
        this.unbilledCallsLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .nav-tabs {
  border-bottom: 1px solid #4a5a6a;
  margin-left: 1.5rem;
  margin-right: 1.5rem;

  .nav-link {
    /* Inactive State: Darker, muted, visually 'behind' */
    color: #8898aa;
    background-color: rgba(0, 0, 0, 0.2); 
    border: 1px solid transparent;
    border-bottom: none;
    margin-right: 4px;
    margin-bottom: -1px;
    font-weight: 500;
    padding: 0.8rem 1.5rem;
    border-radius: 4px 4px 0 0;
    transition: all 0.2s ease;

    &:hover {
      /* Hover State: Slightly lighter, hinting interaction */
      color: #d0d0d0;
      background-color: rgba(255, 255, 255, 0.05);
      border-color: #4a5a6a;
      border-bottom: none;
    }

    &.active {
      /* Active State: Bright, connected to content, distinct highlight */
      color: #ffffff;
      background-color: var(--component-panel-bg, #1e2634);
      border: 1px solid #4a5a6a;
      border-bottom-color: var(--component-panel-bg, #1e2634); 
      box-shadow: inset 0 3px 0 0 #00ceff; /* Strong top accent */
      font-weight: 600; /* Make text bolder */
    }
  }
}

::v-deep .tab-content {
  margin-top: 0 !important;
  
  .panel {
    border: 1px solid #4a5a6a; 
    border-top: none;
    margin-top: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    /* Ensure panel background matches active tab to create the 'merged' effect */
    background-color: var(--component-panel-bg, #1e2634); 
  }
}

.billing-widgets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  padding: 1rem 0 2rem 0;
}

.billing-widget {
  background: var(--component-panel-bg, #1e2634);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
  }

  .widget-icon {
    font-size: 1.8rem;
    width: 50px;
    height: 50px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    flex-shrink: 0;
  }

  .widget-content {
    display: flex;
    flex-direction: column;
    z-index: 2;
  }

  .widget-label {
    font-size: 0.85rem;
    color: #8898aa;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.25rem;
  }

  .widget-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.2;
  }

  .widget-subtext {
    font-size: 0.8rem;
    color: #8898aa;
    margin-top: 2px;
  }

  /* Decorative background element */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    filter: blur(40px);
    opacity: 0.15;
    z-index: 1;
  }
}

/* Specific Widget Styles */
/* Shared Decoration Style */
.widget-decoration {
  position: absolute;
  right: 15px;
  bottom: 15px;
  font-size: 4rem;
  opacity: 0.08;
  transform: rotate(-15deg);
  pointer-events: none;
  z-index: 1;
}

/* Specific Widget Styles */
.widget-balance {
  .widget-icon {
    background: rgba(0, 255, 97, 0.1);
    color: #00ff61;
  }
  &::before { background: #00ff61; }
  border-left: 4px solid #00ff61;
  
  .widget-decoration {
    color: #00ff61;
  }
}

.widget-unpaid {
  .widget-icon {
    background: rgba(255, 71, 87, 0.1);
    color: #ff4757;
  }
  &::before { background: #ff4757; }
  border-left: 4px solid #ff4757;

  .widget-decoration {
    color: #ff4757;
  }
}

.widget-calls {
  .widget-icon {
    background: rgba(46, 134, 222, 0.1);
    color: #2e86de;
  }
  &::before { background: #2e86de; }
  border-left: 4px solid #2e86de;
  
  .widget-decoration {
    color: #2e86de;
  }
}

.clickable-widget {
  cursor: pointer;

  &:hover {
    border-color: rgba(255, 255, 255, 0.15);
  }
}

.widget-pending {
  .widget-icon {
    background: rgba(0, 206, 255, 0.1);
    color: #00ceff;
  }
  &::before { background: #00ceff; }
  border-left: 4px solid #00ceff;

  .widget-decoration {
    color: #00ceff;
  }
}

.balance-breakdown {
  .breakdown-row {
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 0;
  }

  .breakdown-label {
    color: #8898aa;
    font-size: 0.95rem;
  }

  .breakdown-value {
    font-weight: 600;
    font-size: 1rem;
    color: #ffffff;
  }

  .breakdown-divider {
    border-color: rgba(255, 255, 255, 0.1);
    margin: 0.25rem 0;
  }

  .breakdown-alert {
    font-size: 0.85rem;
    font-weight: 600;
  }
}

</style>
